// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import values from 'lodash/values';
import numeral from 'numeral';
import {
  AFRICAN_CORAL,
  COIN_YELLOW,
  GREEN,
  IDEAL_TEAL,
  TRUE_BLUE,
} from '@nerdwallet/react-colors';

import miniFormPw from '../total-rewards/mini-form-pw';
import miniForm from '../total-rewards/mini-form';
import pageContent from '../total-rewards/page-content';

const formatDollarValue = (amount) =>
  `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const summarizeDollarValue = (amount) => {
  const formatStr = amount > 1000000 || amount < 10000 ? '$0.0a' : '$0a';

  return numeral(amount).format(formatStr).toUpperCase();
};
const perksTotal = values(pageContent.perks).reduce(
  (acc, perk) => acc + perk.amount,
  0
);
const getHealthData = (provider, coverage) =>
  get(pageContent, `health.${provider}.${coverage}`, {});
const getHealthTotal = (provider, coverage) => {
  const healthData = getHealthData(provider, coverage);

  return values(healthData).reduce((acc, val) => acc + val, 0);
};

const getDentalData = (provider, coverage) =>
  get(pageContent, `dental.${provider}.${coverage}`, {});
const getDentalTotal = (provider, coverage) => {
  const dentalData = getDentalData(provider, coverage);

  return values(dentalData).reduce((acc, val) => acc + val, 0);
};

export const scrollControl = {
  // Bind the scroll control to the presence of salary
  mapStateToProps({ forms }) {
    const salary = get(forms, 'totalRewards.salary');

    return {
      scrollListener: !!salary,
    };
  },
};

export const passwordLock = {
  // Make password lock listen to user's entry in password form
  mapStateToProps({ forms }) {
    return {
      password: get(forms, 'totalRewardsPasswordForm.totalRewardsPassword'),
    };
  },
};

export const passwordForm = {
  injectProps: {
    formName: 'totalRewardsPasswordForm',
    ...miniFormPw,
  },
};

export const formContainerWrapper = {
  // Toggles size of module holding offer form based on whether salary has been entered
  mapStateToProps({ forms }) {
    const salary = get(forms, 'totalRewards.salary');
    const liveClass = salary
      ? 'total-rewards-form-full'
      : 'total-rewards-form-partial';

    return {
      className: `total-rewards-form-cont ${liveClass}`,
    };
  },
};

export const formContainer = {
  // Toggles size of offer form itself based on whether salary has been entered
  mapStateToProps({ forms }) {
    const salary = get(forms, 'totalRewards.salary');

    return {
      className: salary
        ? 'total-rewards-form-cont-partial'
        : 'total-rewards-form-cont-open',
    };
  },
};

export const offerForm = {
  injectProps: {
    formName: 'totalRewards',
    ...miniForm,
  },
};

export const resultsContainer = {
  // Show container for displaying results if salary has been entered
  mapStateToProps({ forms }) {
    const salary = get(forms, 'totalRewards.salary');

    return {
      className: salary
        ? 'total-rewards-cont-open'
        : 'total-rewards-cont-closed',
    };
  },
};

export const sumText = {
  // Sum all benefits
  mapStateToProps({ forms }) {
    const { salary, dental, health, coverage } = get(forms, 'totalRewards', {});
    const healthTotal =
      getHealthTotal(health, coverage) + getDentalTotal(dental, coverage);
    const fourOhOneK = salary * 0.04;
    const text = formatDollarValue(
      salary + fourOhOneK + healthTotal + perksTotal
    );
    return { text };
  },
};

export const salaryChart = {
  // Settings
  injectProps: {
    defaultExpanded: 'Salary',
    options: {
      legend: {
        show: false,
      },
      donut: {
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
    },
    descriptionData: pageContent.detailText.totalText,
  },
  // Contingent data
  mapStateToProps({ forms }) {
    const { salary, dental, health, coverage } = get(forms, 'totalRewards', {});

    const fourOhOneK = salary * 0.04;

    return {
      data: {
        columns: [
          ['Salary', salary || 0],
          ['Productivity Perks', perksTotal || 0],
          [
            'Health Benefits',
            getHealthTotal(health, coverage) +
              getDentalTotal(dental, coverage) || 0,
          ],
          ['401(k) Match', fourOhOneK || 0],
        ],
        colors: {
          Salary: GREEN,
          'Productivity Perks': IDEAL_TEAL,
          'Health Benefits': COIN_YELLOW,
          '401(k) Match': TRUE_BLUE,
        },
        type: 'donut',
      },
    };
  },
};

export const sumPerks = {
  // Sum up perks
  injectProps: {
    className: 'totals-title',
    text: formatDollarValue(perksTotal),
  },
};

export const perksChart = {
  // All data for this chart is static
  injectProps: {
    defaultExpanded: 'Food',
    data: {
      columns: [
        ['Cell Phone', pageContent.perks.cell.amount],
        ['Commuter Stipend', pageContent.perks.commute.amount],
        ['Enrichment Stipend', pageContent.perks.enrich.amount],
        ['Food', pageContent.perks.food.amount],
        ['Gym', pageContent.perks.gym.amount],
      ],
      colors: {
        'Cell Phone': AFRICAN_CORAL,
        'Commuter Stipend': IDEAL_TEAL,
        'Enrichment Stipend': COIN_YELLOW,
        Food: GREEN,
        Gym: TRUE_BLUE,
      },
      type: 'donut',
    },
    options: {
      legend: {
        show: false,
      },
      donut: {
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
    },
    descriptionData: pageContent.detailText.perkText,
  },
};

export const sumHealth = {
  // Sum up health benefits
  mapStateToProps({ forms }) {
    const { dental, health, coverage } = get(forms, 'totalRewards', {});
    const text = formatDollarValue(
      getHealthTotal(health, coverage) + getDentalTotal(dental, coverage)
    );
    return { text };
  },
};

export const healthChart = {
  // Settings
  injectProps: {
    defaultExpanded: 'Health Insurance',
    options: {
      legend: {
        show: false,
      },
      donut: {
        label: {
          show: false,
        },
      },
      tooltip: {
        show: false,
      },
    },
    descriptionData: pageContent.detailText.healthText,
  },
  // Contingent data
  mapStateToProps({ forms }) {
    const { dental, health, coverage } = get(forms, 'totalRewards', {});
    const { plan, vision } = getHealthData(health, coverage);
    const { dentalplan } = getDentalData(dental, coverage);

    return {
      data: {
        columns: [
          ['Health Insurance', plan || 0],
          ['Dental Insurance', dentalplan || 0],
          ['Vision Insurance', vision || 0],
        ],
        colors: {
          'Health Insurance': GREEN,
          'Dental Insurance': IDEAL_TEAL,
          'Vision Insurance': COIN_YELLOW,
        },
        type: 'donut',
      },
    };
  },
};

export const sumEquity = {
  // Provide num shares entered by user
  mapStateToProps({ forms }) {
    const { shares } = get(forms, 'totalRewards', {});
    const text = Number(shares).toLocaleString('en-US');

    return { text };
  },
};

export const equityChart = {
  // Settings
  injectProps: {
    options: {
      axis: {
        x: {
          label: {
            text: 'Share price',
            position: 'outer-center',
          },
          type: 'category',
          categories: ['$10', '$15', '$20', '$25', '$30', '$35', '$40'],
        },
        y: {
          label: {
            text: 'Total value',
            position: 'outer-middle',
          },
          padding: {
            top: 0,
            bottom: 0,
          },
          tick: {
            format: summarizeDollarValue,
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        show: false,
      },
      grid: {
        x: {
          show: false,
        },
        y: {
          show: true,
        },
      },
    },
  },
  mapStateToProps({ forms }) {
    const { shares } = get(forms, 'totalRewards', {});

    return {
      data: {
        columns: [
          [
            'Total',
            shares * 10,
            shares * 15,
            shares * 20,
            shares * 25,
            shares * 30,
            shares * 35,
            shares * 40,
          ],
        ],
        type: 'bar',
        colors: {
          Total: GREEN,
        },
      },
    };
  },
};
